import { useEffect, useRef, useState } from 'react';
import { CancelledError } from 'LearningManager/helpers/concurrency';

export type ResolveAssignmentUsers = (assignmentId: number) => Promise<string[]>;

export interface AssignmentsUserCount {
	count?: number;
	failedCount: number;
	retry: () => void;
}

const useAssignmentsUserCount = (
	assignmentIds: number[],
	resolve: ResolveAssignmentUsers,
	active: boolean
): AssignmentsUserCount => {
	const [usersById, setUsersById] = useState<Record<number, string[]>>({});
	const [failedIds, setFailedIds] = useState<number[]>([]);
	const [attempt, setAttempt] = useState(0);
	const requested = useRef(new Set<number>());
	const generation = useRef(0);
	const idsKey = assignmentIds.join(',');

	useEffect(() => {
		if (active) return;
		generation.current += 1;
		requested.current = new Set();
		setUsersById({});
		setFailedIds([]);
	}, [active]);

	useEffect(() => {
		if (!active) return;
		const current = generation.current;
		assignmentIds.forEach((id) => {
			if (requested.current.has(id)) return;
			requested.current.add(id);
			resolve(id).then(
				(userIds) => {
					if (current !== generation.current) return;
					setUsersById((prev) => ({ ...prev, [id]: userIds }));
				},
				(error) => {
					if (
						current !== generation.current ||
						error instanceof CancelledError
					) {
						return;
					}
					setFailedIds((prev) => [...prev, id]);
				}
			);
		});
	}, [idsKey, active, attempt]);

	const retry = () => {
		failedIds.forEach((id) => requested.current.delete(id));
		setFailedIds([]);
		setAttempt((prev) => prev + 1);
	};

	const failedCount = assignmentIds.filter((id) => failedIds.includes(id))
		.length;
	const count = assignmentIds.every((id) => usersById[id])
		? new Set(
				([] as string[]).concat(
					...assignmentIds.map((id) => usersById[id])
				)
		  ).size
		: undefined;

	return { count, failedCount, retry };
};

export default useAssignmentsUserCount;
